import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"
import { Chip, Menu, Stack, Typography } from "@mui/material"
import { DateTimePicker } from "@mui/x-date-pickers"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { DateTimeFactory, useDateTime } from "../../../../../../modules/localization/useDateTime"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { createInitEvents } from "../resourceBookingCalendars/helpers"
import { NullableCalendarEvent } from "../calendar/fullCalendarTypes"
import { ResourceFiltersTypes } from "../../../createOrderTypes"
import { ConfirmModal } from "../../../../dialogs/ConfirmModal"
import { DynamicConfigKey } from "../../form/formTypes"
import { roundToNearest5Minutes } from "../../helpers"

const useStyles = makeStyles()((theme) => ({
    chip: {
        borderRadius: ".75rem",
        height: "2.5rem",
        padding: "0 1rem",
        color: theme.palette.common.white,
        "& .MuiChip-label": {
            paddingLeft: ".5rem",
            fontSize: ".875rem",
        },
        "& .MuiChip-deleteIcon": {
            fontSize: "1.25rem",
            marginRight: "0",
            transition: "all ease-out 0.3s",
        },
    },
    selectedChip: {
        background: theme.palette.tailwind.slate[800],
        "&:hover": {
            background: theme.palette.tailwind.slate[900],
        },
        "& .MuiChip-deleteIcon": {
            color: theme.palette.tailwind.slate[300],
            "&:hover": {
                color: theme.palette.tailwind.slate[200],
            },
        },
    },
    unselectedChip: {
        background: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.tailwind.slate[100],
        },
        "& .MuiChip-deleteIcon": {
            color: theme.palette.tailwind.slate[400],
            "&:hover": {
                color: theme.palette.tailwind.slate[500],
            },
        },
        color: theme.palette.common.black,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    dateTimeContainer: {
        padding: theme.spacing(2),
        maxWidth: "18.75rem",
    },
}))

const isSamePeriod = (
    current: { start: string; end: string },
    newStart: DateTime,
    newEnd: DateTime,
    dateTimeFactory: DateTimeFactory,
) => {
    return (
        dateTimeFactory.fromISO(current.start).equals(newStart) && dateTimeFactory.fromISO(current.end).equals(newEnd)
    )
}

type BookPeriodFilterProps = {
    isBookedPeriod: boolean
    setFilters: Dispatch<SetStateAction<ResourceFiltersTypes>>
    isSpecialFlow: boolean
}

export function BookPeriodFilter({ isBookedPeriod, setFilters, isSpecialFlow }: BookPeriodFilterProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const now = dateTimeFactory.now()

    const chipRef = useRef<HTMLDivElement | null>(null)
    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: currentStart }] = useField<DateTime | null>(DynamicConfigKey.MEETING_START_TIME)
    const [{ value: currentEnd }] = useField<DateTime | null>(DynamicConfigKey.MEETING_END_TIME)

    const [confirmCancelChangesDialog, setConfirmCancelChangesDialog] = useState<boolean>(false)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [start, setStart] = useState<DateTime | null>(null)
    const [end, setEnd] = useState<DateTime | null>(null)

    useEffect(() => {
        if (!isSpecialFlow && !isBookedPeriod && event && !event?.resourceIds.length && !start && !end) {
            setStart(currentStart)
            setEnd(currentEnd)
            setFilters((prev) => ({
                ...prev,
                isBookedPeriod: true,
            }))
        }
    }, [])

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleAddFilter = () => {
        setAnchorEl(null)

        if (!start || !end || start >= end) return
        const roundedStart = roundToNearest5Minutes(start)
        const roundedEnd = roundToNearest5Minutes(end)

        setStart(roundedStart)
        setEnd(roundedEnd)
        eventHelpers.setValue(
            createInitEvents(
                [],
                dateTimeFactory.toApiISOMinutesFormat(roundedStart),
                dateTimeFactory.toApiISOMinutesFormat(roundedEnd),
            ),
        )
        setFilters((prev) => ({
            ...prev,
            isBookedPeriod: true,
        }))
        setConfirmCancelChangesDialog(false)
    }

    const handleCloseMenu = () => {
        if (!start || !end) return setAnchorEl(null)

        const roundedStart = roundToNearest5Minutes(start)
        const roundedEnd = roundToNearest5Minutes(end)

        if (roundedStart >= roundedEnd) return
        if (roundedStart < now) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-book-period-past-booking"),
            })
            return
        }

        const alreadySelected = !!(event?.start && event?.end)
        const isSame = alreadySelected && isSamePeriod(event, roundedStart, roundedEnd, dateTimeFactory)
        const hasResources = (event?.resourceIds ?? []).length > 0

        if (isSame) {
            setAnchorEl(null)
        } else if (!hasResources) {
            handleAddFilter()
        } else {
            setConfirmCancelChangesDialog(true)
        }
    }

    const handleRemove = () => {
        setStart(null)
        setEnd(null)
        eventHelpers.setValue(null)
        setFilters((prev) => {
            return {
                ...prev,
                isBookedPeriod: false,
            }
        })
    }

    const handleStartChange = (value: DateTime | null) => {
        if (!value) return setStart(null)

        const rounded = roundToNearest5Minutes(value)
        setStart(rounded)
        if (!end || rounded > end) {
            setEnd(rounded.plus({ minutes: 5 }))
        }
    }

    const handleCloseDialog = () => {
        if (isBookedPeriod) {
            if (event) {
                setStart(dateTimeFactory.fromISO(event.start))
                setEnd(dateTimeFactory.fromISO(event.end))
                setAnchorEl(null)
            }
        } else {
            setStart(null)
            setEnd(null)
            setAnchorEl(null)
        }
        setConfirmCancelChangesDialog(false)
    }

    const hasSelectedValues = start && end

    return (
        <>
            <Chip
                ref={chipRef}
                label={
                    <>
                        {hasSelectedValues ? (
                            dateTimeFactory.formatPeriod(start, end)
                        ) : (
                            <LocalizedStrict id="create-order-timeline-booked-period">
                                <>Book period</>
                            </LocalizedStrict>
                        )}
                    </>
                }
                onClick={handleOpenMenu}
                onDelete={hasSelectedValues ? handleRemove : undefined}
                className={cx(classes.chip, hasSelectedValues ? classes.selectedChip : classes.unselectedChip)}
            />
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
                <Stack className={classes.dateTimeContainer} spacing={2}>
                    <LocalizedStrict
                        id="create-order-timeline-booked-period-start-date-time-label"
                        attrs={{ label: true }}
                    >
                        <DateTimePicker
                            label="Start time"
                            value={start}
                            onChange={handleStartChange}
                            minDateTime={now}
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                        />
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="create-order-timeline-booked-period-end-date-time-label"
                        attrs={{ label: true }}
                    >
                        <DateTimePicker
                            label="End time"
                            value={end}
                            minDateTime={start ? start.plus({ minutes: 5 }) : now.plus({ minutes: 5 })}
                            onChange={(value) => {
                                if (!value) return setEnd(null)
                                setEnd(roundToNearest5Minutes(value))
                            }}
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                        />
                    </LocalizedStrict>
                </Stack>
            </Menu>
            {confirmCancelChangesDialog && (
                <LocalizedStrict
                    id="create-order-book-period-confirm-cancel-changes-dialog"
                    attrs={{ title: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        open={confirmCancelChangesDialog}
                        onClose={handleCloseDialog}
                        title="Remove current selection"
                        buttonLabelSubmit="Confirm"
                        handleClick={handleAddFilter}
                        autoCloseAfterSubmit={false}
                    >
                        <LocalizedStrict id="create-order-book-period-confirm-cancel-changes-dialog-message">
                            <Typography textAlign="center" variant="body1">
                                Changing the period will clear your current selection. Do you want to continue?
                            </Typography>
                        </LocalizedStrict>
                    </ConfirmModal>
                </LocalizedStrict>
            )}
        </>
    )
}
